import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Music2, Mic2, Sparkles, Users, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { defaultSiteSettings } from '@/lib/defaults';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

function buildMailtoBook() {
  const s = defaultSiteSettings;
  const subject = encodeURIComponent('Singvolve: Booking Enquiry - Singing Lessons');
  const body = encodeURIComponent(`I'd like to book singing lessons.\n\nName - Pupil: \nName - Parent/Carer (If applicable): \nEmail: \nTelephone number: \nAre you available on a Tuesday or Thursday evening between 5-7pm to have a 30 minute lesson at the Bristol Cathedral Choir School (BCCS) in Bristol: \nWhat style of music would you like to try (optional): `);
  return `mailto:${s.adminEmail}?subject=${subject}&body=${body}`;
}

export default function Lessons() {
  return (
    <div data-testid="lessons-page" className="bg-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.5 }} className="max-w-3xl mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#F5F0F7] text-[#4B1D5E] text-sm font-medium mb-6">Our Lessons</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2D1B36] mb-6">Singing Lessons for Everyone</h1>
          <p className="text-base sm:text-lg text-[#5E4B66] leading-relaxed">
            Every lesson is a one-to-one experience, tailored to your goals and pace. Whether you're 7 or 70, a beginner or experienced, there's a place for you at Singvolve.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {[
            {
              icon: Users, title: 'Children & Young People',
              desc: 'Fun, engaging lessons that build confidence and a love of singing. We work on breathing, pitch, rhythm, and performing favourite songs in a supportive environment.',
              tags: ['Ages 7+', 'Build confidence', 'All abilities'],
            },
            {
              icon: Mic2, title: 'Adult Beginners',
              desc: "It's never too late to start singing! Whether you've always wanted to sing or are returning after a break, lessons are designed to make you feel at ease from the very first session.",
              tags: ['No experience needed', 'Relaxed pace', 'Encouraging'],
            },
            {
              icon: Music2, title: 'Developing Singers',
              desc: 'For students with some experience who want to refine their technique, expand their range, or prepare for performances, auditions, or exams.',
              tags: ['Technique building', 'Performance prep', 'Repertoire expansion'],
            },
            {
              icon: Sparkles, title: 'What You Will Learn',
              desc: 'Lessons cover breathing technique, vocal warm-ups, pitch accuracy, tone development, rhythm, stage presence, and working on songs across a wide range of genres.',
              tags: ['Pop', 'Musical Theatre', 'Classical', 'Folk'],
            },
          ].map((item, i) => (
            <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}>
              <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 h-full">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-2xl bg-[#F5F0F7] flex items-center justify-center mb-5">
                    <item.icon className="h-7 w-7 text-[#4B1D5E]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-[#2D1B36] mb-3">{item.title}</h3>
                  <p className="text-[#5E4B66] text-sm leading-relaxed mb-4">{item.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {item.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full bg-[#F5F0F7] text-[#4B1D5E] text-xs font-medium">{tag}</span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="bg-[#FAFAFA] rounded-3xl p-8 lg:p-12 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl font-bold text-[#2D1B36] mb-2">Ready to get started?</h3>
            <p className="text-[#5E4B66]">Book a block of lessons or try a free taster session first.</p>
          </div>
          <div className="flex gap-4">
            <a href={buildMailtoBook()}>
              <Button className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-full px-6 py-5 font-semibold" data-testid="lessons-book-cta">
                Book Lessons <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <Link to="/pricing">
              <Button variant="outline" className="border-[#4B1D5E] text-[#4B1D5E] hover:bg-[#F5F0F7] rounded-full px-6 py-5 font-semibold" data-testid="lessons-pricing-cta">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
